import { Link } from '@tanstack/react-router';
import { CalendarDays } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { AUDIT_CATEGORIES, AUDIT_CATEGORY_LABELS, isDelegationOn, type AuditCategory, type AuditSummary } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { DatePicker } from '../../../../components/DatePicker';
import { useModules } from '../../../../lib/modules';
import { PersonPicker, type PersonOption } from '../../../../components/PersonPicker';
import { Popover, usePopover } from '../../../../components/Popover';
import { addDhakaDays, dhakaToday } from '../../../../lib/dhaka-time';
import { formatNumber } from '../../../../lib/format';
import { AUDIT_WINDOW_DAYS, whenLabel, withFilters, type AccessLogSearch } from './access-log-address';
import styles from './AccessLogFilters.module.css';

// USER-06's left column (Access log A): the kinds with their counts, then Who, When and how long entries are kept.
// Under 1180px the same pieces lie in a row above the feed; on a phone the kinds wrap as chips.

interface AccessLogFiltersProps {
  readonly search: AccessLogSearch;
  readonly summary: AuditSummary | undefined;
  readonly people: readonly PersonOption[];
  readonly onChange: (next: AccessLogSearch) => void;
}

export function AccessLogFilters({ search, summary, people, onChange }: AccessLogFiltersProps): ReactNode {
  const set = (patch: Parameters<typeof withFilters>[1]): void => onChange(withFilters(search, patch));
  const isCoverOn = isDelegationOn(useModules());

  return (
    <div className={styles.filters}>
      <section className={styles.group} aria-labelledby="access-log-kinds">
        <h2 id="access-log-kinds" className={styles.heading}>
          What happened
        </h2>
        <div className={styles.kinds}>
          <KindButton label="Everything" count={summary?.total} isOn={search.category === undefined} onPick={() => set({ category: undefined })} />
          {AUDIT_CATEGORIES.map((category: AuditCategory) => {
            const button = (
              <KindButton
                key={category}
                label={AUDIT_CATEGORY_LABELS[category]}
                count={summary?.kinds[category]}
                isOn={search.category === category}
                onPick={() => set({ category })}
              />
            );
            // Cover belongs to Hire requests or Offers (§L34, review R18): with both off, the kind leaves no trace (#13).
            return category === 'delegation' && !isCoverOn ? null : button;
          })}
        </div>
      </section>

      <div className={styles.pickers}>
        <section className={styles.group} aria-labelledby="access-log-who">
          <h2 id="access-log-who" className={styles.heading}>
            Who
          </h2>
          <PersonPicker
            label="Who"
            people={people}
            value={search.actorId ?? null}
            onChange={(actorId) => set({ actorId: actorId ?? undefined })}
            placeholder="Anyone"
            isClearable
          />
        </section>

        <section className={styles.group} aria-labelledby="access-log-when">
          <h2 id="access-log-when" className={styles.heading}>
            When
          </h2>
          <WhenPicker search={search} onChange={set} />
        </section>
      </div>

      <section className={`${styles.group} ${styles.kept}`} aria-labelledby="access-log-kept">
        <h2 id="access-log-kept" className={styles.heading}>
          Kept for
        </h2>
        <p className={styles.note}>
          As long as <Link to="/admin/retention">Retention</Link> says. Entries can&apos;t be edited or deleted here.
        </p>
      </section>
    </div>
  );
}

function KindButton({
  label,
  count,
  isOn,
  onPick,
}: {
  readonly label: string;
  readonly count: number | undefined;
  readonly isOn: boolean;
  readonly onPick: () => void;
}): ReactNode {
  return (
    <button type="button" className={styles.kind} aria-pressed={isOn} onClick={onPick}>
      <span className={styles.kindLabel}>{label}</span>
      <span className={styles.count}>{count === undefined ? '—' : formatNumber(count)}</span>
    </button>
  );
}

/** "When": the last 7, 30 or 90 days, or two dates. Opens beside its button. */
function WhenPicker({
  search,
  onChange,
}: {
  readonly search: AccessLogSearch;
  readonly onChange: (patch: Parameters<typeof withFilters>[1]) => void;
}): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const today = dhakaToday();
  const [from, setFrom] = useState(search.from ?? '');
  const [to, setTo] = useState(search.to ?? '');
  const isRange = search.from !== undefined || search.to !== undefined;
  const isBackwards = from !== '' && to !== '' && to < from;

  const open = (): void => {
    setFrom(search.from ?? addDhakaDays(today, -29));
    setTo(search.to ?? today);
    toggle();
  };
  const pickWindow = (days: (typeof AUDIT_WINDOW_DAYS)[number]): void => {
    onChange({ days: days === 30 ? undefined : days });
    close();
  };
  const applyRange = (): void => {
    if (isBackwards) return;
    onChange({ from: from === '' ? undefined : from, to: to === '' ? undefined : to });
    close();
  };

  return (
    <>
      <button ref={triggerRef} type="button" className={styles.when} onClick={open} aria-haspopup="dialog" aria-expanded={anchor !== null}>
        <span className={styles.whenText}>{whenLabel(search)}</span>
        <CalendarDays size={15} aria-hidden />
      </button>
      <Popover anchor={anchor} onClose={close} label="When">
        <div className={styles.whenPanel}>
          <div className={styles.windows}>
            {AUDIT_WINDOW_DAYS.map((days) => (
              <button
                key={days}
                type="button"
                className={styles.window}
                aria-pressed={!isRange && (search.days ?? 30) === days}
                onClick={() => pickWindow(days)}
              >
                Last {days} days
              </button>
            ))}
          </div>
          <p className={styles.panelHeading}>Or pick the dates</p>
          <div className={styles.dates}>
            <label className={styles.dateField}>
              <span className={styles.dateLabel}>From</span>
              <DatePicker label="From" value={from} max={today} onChange={setFrom} />
            </label>
            <label className={styles.dateField}>
              <span className={styles.dateLabel}>Until</span>
              <DatePicker label="Until" value={to} max={today} onChange={setTo} isInvalid={isBackwards} />
            </label>
          </div>
          {isBackwards && <p className={styles.error}>Until can&apos;t be before From.</p>}
          <div className={styles.panelActions}>
            <Button variant="solid" size="sm" onClick={applyRange} disabled={isBackwards || (from === '' && to === '')}>
              Show these dates
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}
